"use client";

import { type ReactNode, useRef, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, LABEL } from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";

/** Every label `ChartFigure` shows. Widgets pass their own strings. */
export interface ChartFigureLabels {
  downloadSvg: string;
  downloadPng: string;
  copyData: string;
  copied: string;
  exportFailed: string;
  /** The data-table toggle; needed only when a `table` is passed. */
  showDataTable?: string;
  /** "Figure {number}" */
  figureLabel: string;
  /** "Note." — italic, before the figure note, as APA sets it. */
  noteLabel: string;
  copyCaption: string;
}

export interface ChartTable {
  head: readonly string[];
  rows: readonly (readonly (string | number)[])[];
}

const HTML_SPECIAL = /[&<>"]/g;
const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
};

function escapeHtml(text: string): string {
  return text.replace(HTML_SPECIAL, (char) => HTML_ESCAPES[char] ?? char);
}

/**
 * A chart with everything around it: the APA caption, the image, the note, the export
 * buttons and the data table.
 *
 * With `apa` on, the figure number (bold) and title (italic) are set above the image
 * and the note below it, in the page rather than in the picture — APA 7 places them in
 * the paper's text, so the exported image carries neither. "Copy caption" puts both on
 * the clipboard with their formatting for pasting into Word or Google Docs.
 *
 * The export buttons find the `<svg>` inside `children` when clicked, so a widget just
 * renders its chart here and needs no ref.
 */
export function ChartFigure({
  apa,
  figureNumber,
  title,
  note,
  filename,
  data,
  table,
  labels,
  children,
}: {
  apa: boolean;
  figureNumber: string;
  title: string;
  note: string;
  /** Base name for downloads, without extension. */
  filename: string;
  /** Tab-separated data for "Copy data" — pastes straight into a spreadsheet. */
  data: string;
  /** The chart's data as a table, for readers who cannot see the image. */
  table: ChartTable | null;
  labels: ChartFigureLabels;
  children: ReactNode;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const figureLabel = t(labels.figureLabel, { number: figureNumber.trim() });
  const svg = () => holder.current?.querySelector("svg") ?? null;

  function saveSvg() {
    const element = svg();
    if (element) {
      downloadSvg(`${filename}.svg`, element);
    }
  }

  async function savePng() {
    const element = svg();
    if (!element) {
      return;
    }
    try {
      setFailed(false);
      await downloadPng(`${filename}.png`, element, { scale: 2 });
    } catch {
      setFailed(true);
    }
  }

  return (
    <figure className="space-y-3">
      {apa ? (
        <figcaption className="space-y-1 text-foreground">
          <p className="font-bold">{figureLabel}</p>
          {title.trim() ? <p className="italic">{title}</p> : null}
        </figcaption>
      ) : null}
      <div
        className="overflow-hidden rounded-lg border border-border bg-white"
        ref={holder}
      >
        {children}
      </div>
      {apa && note.trim() ? (
        <p className="text-foreground text-sm">
          <em>{labels.noteLabel}</em> {note}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_SECONDARY} onClick={saveSvg} type="button">
          {labels.downloadSvg}
        </button>
        <button className={BUTTON_SECONDARY} onClick={savePng} type="button">
          {labels.downloadPng}
        </button>
        <CopyButton
          copiedLabel={labels.copied}
          label={labels.copyData}
          text={data}
        />
        {apa ? (
          <CopyButton
            copiedLabel={labels.copied}
            html={`<p><strong>${escapeHtml(figureLabel)}</strong></p><p><em>${escapeHtml(title)}</em></p>`}
            label={labels.copyCaption}
            text={`${figureLabel}\n${title}`}
          />
        ) : null}
      </div>
      {failed ? <ErrorNotice>{labels.exportFailed}</ErrorNotice> : null}
      {table && table.rows.length > 0 && labels.showDataTable ? (
        <details className="group">
          <summary className={`${LABEL} cursor-pointer`}>
            {labels.showDataTable}
          </summary>
          <div className="mt-3">
            <DataTable head={table.head} rows={table.rows} />
          </div>
        </details>
      ) : null}
    </figure>
  );
}

/** Rows as tab-separated text, numbers in plain machine notation for spreadsheets. */
export function toTsv(
  rows: readonly (readonly (string | number | null)[])[]
): string {
  return rows
    .map((row) =>
      row.map((cell) => (cell === null ? "" : String(cell))).join("\t")
    )
    .join("\n");
}
